<script setup>
import { ref, onMounted, onUnmounted, defineEmits, computed } from 'vue';
import { useRouter } from 'vue-router';
import feather from 'feather-icons';
import { useStorage } from '@/composables/use-storage.js';
import { useIcons } from '@/composables/icons.js';
import TextInput from '../forms/inputs/text-input.vue';
import { handleMarketplaceUrl } from '@/services/api';
import axios from '@/plugins/axios';

const marketplaceUrl = handleMarketplaceUrl();

// Composables
const router = useRouter();
const { userInfo, clearUserData } = useStorage();
const { User, Wallet } = useIcons();
const searchQuery = ref('');
const lastSearchTerm = ref('');

const emit = defineEmits(['reset-filters', 'search']);

// Reactive state
const toggle = ref(false);
const activeIndex = ref('');
const dropdownOpen = ref(false);
const dropdownToggle = ref(null);
const userInfos = ref(userInfo.value);
const profilePicture = ref(userInfos?.value?.profile_pic);

const checkScreenSize = () => {
    if (window.innerWidth >= 1024 && toggle.value) {
        toggle.value = false;
        document.body.style.overflow = '';
    }
};

// Lifecycle hooks
onMounted(() => {
    activeIndex.value = window.location.pathname;
    
    window.addEventListener('resize', checkScreenSize);
    document.addEventListener('click', handleClickOutside);
    feather.replace();
    scrollToTop();

    const savedTheme = localStorage.getItem('theme') || 'dark';
    document.getElementsByTagName("html")[0].className = savedTheme;

    window.addEventListener('profile-updated', handleProfileUpdate);
});

onUnmounted(() => {
    
    window.removeEventListener('resize', checkScreenSize);
    document.removeEventListener('click', handleClickOutside);
    window.removeEventListener('profile-updated', handleProfileUpdate);
});

// Methods
const handler = () => {
    toggle.value = !toggle.value;
    // Prevent body scroll when menu is open
    if (toggle.value) {
        document.body.style.overflow = 'hidden';
    } else {
        document.body.style.overflow = '';
    }
};


const handleClickOutside = (event) => {
    if (!dropdownToggle.value.contains(event.target)) {
        dropdownOpen.value = false;
    }
};

const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
};

const logout = async () => {
    try {
        clearUserData();
        const response = await axios.post(marketplaceUrl + '/explore/api/logout', {});

        if (response.status === 200) {
            window.location.href = marketplaceUrl + '/explore/login';
        }
    } catch (error) {
        console.error("Error logging out:", error);
    }
};

const handleProfileUpdate = (event) => {
    if (event.detail && event.detail.profilePicture) {
        profilePicture.value = event.detail.profilePicture;
    } else {
        profilePicture.value = userInfos?.value?.profile_pic ?? null;
    }

    userInfos.value = userInfo.value;
};

// const handleNavigateToExplore = () => {
//     if (router.currentRoute.value.path === marketplaceUrl + '/explore') {
//         emit('reset-filters');
//     }
// };

const adminRoutes = computed(() => {
    let result = [];
    // Routes admin with permissions
    const userGroupPermissions = ['group-manager', 'group-set-users', 'group-flags'];
    const listingDatasetPermissions = ['tags-manager'];
    if (userGroupPermissions.some(permission => userInfos.value?.permissions.includes(permission))) {
        result.push({
            icon: 'uil-users-alt',
            label: 'User Groups',
            path: marketplaceUrl + '/explore/admin/user-groups'
        });
    }
    if (listingDatasetPermissions.some(permission => userInfos.value?.permissions.includes(permission))) {
        result.push({
            icon: 'uil-folder-open',
            label: 'Listings & Datasets',
            path: marketplaceUrl + '/explore/admin/listings-datasets'
        });
    }

    // Routes admin without permissions
    const { userInfo } = useStorage();
    const isAdmin = userInfo.value?.is_admin === true;
    if (isAdmin) {
        result.push(
            {
                icon: 'uil-cog',
                label: 'System Settings',
                path: marketplaceUrl + '/explore/admin/system-settings'
            },
            {
                icon: 'uil-balance-scale',
                label: 'RFP Arbitration',
                path: marketplaceUrl + '/explore/admin/rfp-arbitration'
            }
        );
    }
    return result;
})

const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    }
};

const filterItems = (value) => {
    if (!value || !value.trim()) {
        emit('search', '');
        return;
    }

    const searchValue = value.trim();
    emit('search', searchValue);

    if (router.currentRoute.value.path !== marketplaceUrl + '/explore') {
        router.push(marketplaceUrl + '/explore');
    }
};

const debouncedSearch = debounce((value) => {
    if (value === lastSearchTerm.value) return;
    if (value.length === 0 || value.length >= 2) {
        lastSearchTerm.value = value;
        filterItems(value);
    }
}, 300);

const handleSearch = () => {
    debouncedSearch(searchQuery.value);
};

const clearSearch = () => {
    searchQuery.value = '';
    lastSearchTerm.value = '';
    emit('search', searchQuery.value);
};
</script>

<template>
    <!-- Start Navbar -->
    <nav class="relative overflow-visible z-[9998] h-16 flex items-center w-full backdrop-blur-md bg-black/50">
        <!-- Black Navbar Background -->
        <div class="absolute inset-0"></div>
        <!-- Subtle border -->
    
        <div class="relative px-[48px] z-10 overflow-visible w-full">
            <div class="flex items-center justify-between gap-4 relative">
                <!-- Left section: Logo -->
                <div class="flex-shrink-0">
                    <router-link :to="{ name: 'landing' }" class="block">
                        <img src='../../assets/images/avatar/orb_avatar.png' class="block lg:hidden w-6 h-6" alt=""
                            loading="lazy">
                        <div class="hidden lg:block">
                            <img src="../../assets/images/logo-dark.png" class="h-10 w-auto dark:hidden" alt=""
                                loading="lazy">
                            <img src="../../assets/images/logo-dark.png" class="h-6 w-auto hidden dark:block" alt=""
                                loading="lazy">
                        </div>
                    </router-link>
                </div>

                <!-- Center section: Navigation and Search -->
                <div class="hidden lg:flex flex-grow items-center justify-between gap-6 mx-6">
                    <!-- Search input -->
                    <div class="flex-grow max-w-2xl">
                        <TextInput v-model="searchQuery" id="navbar-search"
                            placeholder="Search all datasets, modalities, use cases..."
                            class="w-full navbar-search-input" @input="handleSearch">
                            <template #icon>
                                <i class="uil uil-search text-sm"></i>
                            </template>
                            <template #right-icon v-if="searchQuery">
                                <i class="uil uil-times cursor-pointer text-sm" @click="clearSearch"></i>
                            </template>
                        </TextInput>
                    </div>
                    <!-- Navigation menu -->
                    <ul class="flex items-center gap-6">
                        <li>
                            <a :href="marketplaceUrl + '/explore'" @click="handleNavigateToExplore"
                                class="flex items-center text-sm font-medium text-white/80 hover:text-white transition-all duration-200 rounded-lg hover:bg-white/5 px-3 py-2">
                                Datalab
                            </a>
                        </li>
                        <li>
                            <a href="https://docs.portexai.com/" target="_blank" rel="noopener noreferrer"
                                class="flex items-center text-sm font-medium text-white/80 hover:text-white transition-all duration-200 rounded-lg hover:bg-white/5 px-3 py-2">
                                Docs
                            </a>
                        </li>
                        <li>
                            <a href="https://research.portexai.com/" target="_blank" rel="noopener noreferrer"
                                class="flex items-center text-sm font-medium text-white/80 hover:text-white transition-all duration-200 rounded-lg hover:bg-white/5 px-3 py-2">
                                Research
                            </a>
                        </li>
                    </ul>
                </div>

                <!-- Right section: User Controls -->
                <div class="flex items-center gap-3">
                    <!-- Mobile menu toggle -->
                    <div class="lg:hidden" @click="handler">
                        <button
                            class="p-2 focus:outline-none rounded-lg transition-all duration-300 hover:bg-gradient-to-r hover:from-slate-600/20 hover:to-slate-500/20"
                            :class="toggle ? 'text-slate-200 bg-gradient-to-r from-slate-600/30 to-slate-500/30' : 'text-gray-100'"
                            aria-label="Toggle navigation menu">
                            <i class="uil uil-bars text-2xl"></i>
                        </button>
                    </div>
                    <!-- Wallet connect button -->
                    <div class="hidden sm:inline-block" v-if="userInfos">
                        <a @click="connect" href="#!" id="connectWallet"
                            :class="isConnected ? 'bg-gradient-to-tr from-emerald-500 to-emerald-700 hover:from-emerald-600 hover:to-emerald-800 border-emerald-600 hover:border-emerald-700' : 'bg-gradient-to-tr from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 border-red-600 hover:border-red-700'"
                            class="btn btn-icon rounded-full text-white group relative transition-all duration-300"
                            aria-label="Connect wallet">
                            <Wallet class="w-full h-full p-2 text-gray-100" />
                            <div
                                class="absolute -bottom-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-echoes-background-gray text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                {{ isConnected ? 'Wallet Connected' : 'Connect your wallet' }}
                            </div>
                        </a>
                    </div>

                    <!-- User profile dropdown -->
                    <div class="dropdown relative" style="z-index: 99999;">
                        <button ref="dropdownToggle" @click="dropdownOpen = !dropdownOpen"
                            class="dropdown-toggle btn btn-icon border-none rounded-full bg-gradient-to-tr from-slate-400 to-slate-600 hover:from-slate-300 hover:to-slate-500 border-slate-500 hover:border-slate-400 transition-all duration-300 group relative shadow-lg hover:shadow-xl hover:shadow-slate-500/30"
                            type="button" :aria-label="userInfos ? 'Open user profile menu' : 'Open login menu'"
                            aria-haspopup="true" :aria-expanded="dropdownOpen">
                            <div class="w-10 h-10 overflow-hidden">
                                <img v-if="profilePicture" :src="profilePicture" alt="Profile avatar"
                                    class="rounded-full w-full h-full p-[1px] object-cover shadow-inner" width="40"
                                    height="40">
                                <div v-else class="w-full h-full p-2 text-gray-100">
                                    <User />
                                </div>
                            </div>
                            <div
                                class="absolute -bottom-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-echoes-background-gray text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                {{ userInfos ? 'My profile' : 'Login' }}
                            </div>
                        </button>

                        <!-- Dropdown menu -->
                        <div class="dropdown-menu absolute end-0 m-0 mt-2 z-[9999] w-48 rounded-md overflow-hidden bg-echoes-background-black"
                            v-show="dropdownOpen">
                            <!-- Authenticated user view -->
                            <div v-if="userInfos">
                                <div class="relative">
                                    <div class="py-6 bg-gradient-to-tr from-slate-400 to-slate-600"></div>
                                    <div class="absolute px-4 -bottom-8 start-0">
                                        <div class="flex items-center gap-2">
                                            <img v-if="profilePicture" :src="profilePicture" alt=""
                                                class="rounded-full size-12" width="48"
                                                height="48">
                                            <div v-else
                                                class="p-2 w-[3rem] h-[3rem] text-echoes-background-gray rounded-full bg-echoes-background-black">
                                                <User />
                                            </div>
                                            <span class="font-semibold text-[15px] mt-3 truncate">@{{ userInfos?.username || ''
                                                }}</span>
                                        </div>
                                    </div>
                                </div>

                                <ul class="py-2 mt-8 text-start divide-y divide-echoes-border-gray">
                                    <!-- Profile Section -->
                                    <li>
                                        <div class="mb-2">
                                            <a :href="marketplaceUrl + '/explore/creator-profile'"
                                                class="flex items-center gap-2 py-2 px-3 hover:bg-echoes-background-gray/50 hover:text-white transition-all duration-200">
                                                <div class="flex-shrink-0">
                                                    <i class="uil uil-plus text-[18px] text-echoes-primary"></i>
                                                </div>
                                                <div>
                                                    <p class="text-sm font-medium text-white/80 hover:text-white transition-all duration-200">Data Studio</p>

                                                </div>
                                            </a>
                                        </div>
                                    </li>

                                    <!-- Account Section -->
                                    <li>
                                        <div class="mb-2">
                                            <p
                                                class="px-2 py-1.5 text-xs font-semibold text-white uppercase">
                                                Account</p>
                                            <a v-if="userInfos?.permissions.includes('buy')" :href="marketplaceUrl + '/explore/creator-profile?tab=purchases'"
                                                class="flex items-center gap-2 py-2 px-3 hover:bg-echoes-background-gray/50 hover:text-white transition-all duration-200">
                                                <i class="uil uil-shopping-cart text-[18px] text-echoes-primary"></i>
                                                <span class="text-sm font-medium">Purchases</span>
                                            </a>
                                            <a v-if="userInfos?.permissions.includes('listings')"
                                                :href="marketplaceUrl + '/explore/creator-profile?tab=sales'"
                                                class="flex items-center gap-2 py-2 px-3 hover:bg-echoes-background-gray/50 hover:text-white transition-all duration-200">
                                                <i class="uil uil-usd-circle text-[18px] text-echoes-primary"></i>
                                                <span class="text-sm font-medium">Sales</span>
                                            </a>
                                            <a :href="marketplaceUrl + '/explore/creator-profile-edit'"
                                                class="flex items-center gap-2 py-2 px-3 hover:bg-echoes-background-gray/50 hover:text-white transition-all duration-200">
                                                <i class="uil uil-setting text-[18px] text-echoes-primary"></i>
                                                <span class="text-sm font-medium">Settings</span>
                                            </a>
                                        </div>
                                    </li>

                                    <!-- Admin Section -->
                                    <li v-if="adminRoutes.length > 0">
                                        <div class="mb-2">
                                            <p
                                                class="px-2 py-1.5 text-xs font-semibold text-white uppercase">
                                                Admin</p>
                                            <a v-for="route in adminRoutes" :key="route.path" :href="route.path"
                                                class="flex items-center gap-2 py-2 px-3 hover:bg-echoes-background-gray/50 hover:text-white transition-all duration-200">
                                                <i :class="[route.icon, 'text-[18px] text-echoes-primary']"></i>
                                                <span class="text-sm font-medium">{{ route.label }}</span>
                                            </a>
                                        </div>
                                    </li>

                                    <!-- Logout Section -->
                                    <li>
                                        <div class="pt-2">
                                            <a @click="logout" :href="marketplaceUrl + '/explore/login'"
                                                class="flex items-center gap-2 py-2 px-3 text-red-500 hover:bg-red-600/20 transition-all duration-200">
                                                <i class="uil uil-sign-out-alt text-[18px]"></i>
                                                <span class="text-sm font-medium">Logout</span>
                                            </a>
                                        </div>
                                    </li>
                                </ul>
                            </div>

                            <!-- Guest user view -->
                            <ul class="py-2 text-start" v-else>
                                <li>
                                    <a @click="logout" :href="marketplaceUrl + '/explore/login'"
                                        class="block text-[14px] font-semibold py-2 px-4 text-white/80 hover:text-white transition-all duration-200 hover:bg-echoes-background-gray/50 rounded-lg">
                                        <i class="uil uil-sign-in-alt text-[16px] align-middle me-2"></i>Login
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div v-if="userInfos">
                        <NotificationsBell />
                    </div>


                </div>
            </div>

            <!-- Mobile Navigation Menu -->
            <div v-show="toggle" class="lg:hidden fixed inset-0 z-[100] overflow-hidden" style="top: 0; height: 100vh;">
                <!-- Mobile Menu Background -->
                <div class="absolute inset-0 bg-black"></div>
                <div class="flex flex-col h-full relative z-10">
                    <!-- Mobile Header -->
                    <div
                        class="flex items-center justify-between border-b border-echoes-border-gray p-4 sticky top-0 bg-black z-10">
                        <a :href="marketplaceUrl + '/explore'" @click="handler" class="flex items-center flex-shrink-0">
                            <img src='../../assets/images/avatar/orb_avatar.png' class="w-12 h-12" alt="" width="48"
                                height="48" loading="lazy">
                        </a>

                        <div v-if="router.currentRoute.value.path === '/explore'" class="flex-1 mx-4">
                            <TextInput v-model="searchQuery" id="navbar-search-mobile"
                                placeholder="Search all datasets..." class="w-full" @input="handleSearch">
                                <template #icon>
                                    <i class="uil uil-search"></i>
                                </template>
                                <template #right-icon v-if="searchQuery">
                                    <i class="uil uil-times cursor-pointer" @click="clearSearch"></i>
                                </template>
                            </TextInput>
                        </div>

                        <button @click="handler"
                            class="p-2 focus:outline-none text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex-shrink-0">
                            <i class="uil uil-times text-2xl text-gray-100"></i>
                        </button>
                    </div>

                    <!-- Mobile Navigation Links -->
                    <div class="flex-1 overflow-y-auto">
                        <ul class="py-4">
                            <li>
                                <a :href="marketplaceUrl + '/explore'" @click="handler"
                                    class="flex items-center px-4 py-3 font-medium hover:bg-gradient-to-r hover:from-slate-600/20 hover:to-slate-500/20 transition-all duration-300 text-gray-100 hover:text-slate-200 rounded-lg mx-2"
                                    :class="activeIndex === '/explore' ? 'text-slate-200 bg-gradient-to-r from-slate-600/30 to-slate-500/30 shadow-md shadow-slate-500/20' : ''">

                                    Datalab
                                </a>
                            </li>
                            <li>
                                <a href="https://docs.portexai.com/" target="_blank" rel="noopener noreferrer"
                                    class="flex items-center px-4 py-3 font-medium hover:bg-gradient-to-r hover:from-slate-600/20 hover:to-slate-500/20 transition-all duration-300 text-gray-100 hover:text-slate-200 rounded-lg mx-2">

                                    Docs
                                </a>
                            </li>
                            <li>
                                <a href="https://research.portexai.com/" target="_blank" rel="noopener noreferrer"
                                    class="flex items-center px-4 py-3 font-medium hover:bg-gradient-to-r hover:from-slate-600/20 hover:to-slate-500/20 transition-all duration-300 text-gray-100 hover:text-slate-200 rounded-lg mx-2">

                                    Research
                                </a>
                            </li>
                        </ul>

                        <!-- Mobile Wallet Button -->
                        <div class="p-4 mt-auto border-t border-gray-800/50 sticky bottom-0 bg-black" v-if="userInfos">
                            <a @click="connect" href="#!"
                                :class="isConnected ? 'bg-gradient-to-tr from-emerald-500 to-emerald-700' : 'bg-gradient-to-tr from-red-500 to-red-700'"
                                class="flex items-center justify-center w-full text-sm py-3 rounded-lg text-white font-medium space-x-2">
                                <Wallet class="w-5 h-5" />
                                <span>{{ isConnected ? 'Wallet Connected' : 'Connect Wallet' }}</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </nav>
    <!-- End Navbar -->
</template>

